import { Chip, Tooltip } from '@mui/material';
import { differenceInCalendarDays, differenceInMinutes } from 'date-fns';

// Server-computed deadline status (see dieter-server src/utils/rightsRequestDeadline.ts
// and incidentDeadline.ts - the status is authoritative, this component only derives
// display labels from it).
export type RightsDeadlineStatus = 'OPEN' | 'DUE_SOON' | 'CRITICAL' | 'OVERDUE' | 'DONE' | 'UNKNOWN';
export type DeadlineBadgeStatus = RightsDeadlineStatus | 'NOT_REQUIRED';

interface Props {
  status: DeadlineBadgeStatus;
  deadline?: string | Date | null;
  completedAt?: string | Date | null;
  isExtended?: boolean;
  // 'days' for the one-month answer deadline (Art. 12 Abs. 3), 'hours' for the
  // 72h breach reporting deadline (Art. 33 Abs. 1)
  scale?: 'days' | 'hours';
  // verb for the DONE label, e.g. 'erledigt' (default) or 'gemeldet'
  doneVerb?: string;
  tooltip?: string;
}

const COLORS: Record<RightsDeadlineStatus, { bg: string; fg: string }> = {
  OPEN: { bg: '#EDF3DB', fg: '#5C7025' },
  DUE_SOON: { bg: '#FCF3C7', fg: '#8A6D00' },
  CRITICAL: { bg: '#FDE3E9', fg: '#C22E55' },
  OVERDUE: { bg: '#FDE3E9', fg: '#C22E55' },
  DONE: { bg: '#EEEEEE', fg: '#666666' },
  UNKNOWN: { bg: 'transparent', fg: '#888888' },
};

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

function formatDateTime(date: Date): string {
  return `${date.toLocaleDateString('de-DE')}, ${date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`;
}

export function deadlineLabel({ status, deadline, completedAt, isExtended, scale = 'days', doneVerb = 'erledigt' }: Props): string {
  const deadlineDate = toDate(deadline);
  const completedDate = toDate(completedAt);

  if (status === 'DONE') {
    return completedDate ? `${doneVerb} am ${completedDate.toLocaleDateString('de-DE')}` : doneVerb;
  }
  if (status === 'UNKNOWN' || !deadlineDate) {
    return 'Frist unbekannt';
  }

  const prefix = isExtended ? 'verlängert · ' : '';

  if (scale === 'hours') {
    const hours = Math.trunc(differenceInMinutes(deadlineDate, new Date()) / 60);
    const until = formatDateTime(deadlineDate);
    if (hours < 0) return `${prefix}überfällig · seit ${until}`;
    if (hours === 0) return `${prefix}weniger als 1 Std. · bis ${until}`;
    return `${prefix}noch ${hours} Std. · bis ${until}`;
  }

  const days = differenceInCalendarDays(deadlineDate, new Date());
  const until = deadlineDate.toLocaleDateString('de-DE');

  if (days < 0) {
    const overdueDays = Math.abs(days);
    return `${prefix}${overdueDays} ${overdueDays === 1 ? 'Tag' : 'Tage'} überfällig · seit ${until}`;
  }
  if (days === 0) return `${prefix}heute fällig`;
  if (days === 1) return `${prefix}morgen fällig`;
  return `${prefix}noch ${days} Tage · bis ${until}`;
}

export function DeadlineBadge(props: Props) {
  const { status, scale = 'days' } = props;
  if (status === 'NOT_REQUIRED') return null;

  const colors = COLORS[status] || COLORS.UNKNOWN;
  const label = deadlineLabel(props);

  const tooltip =
    props.tooltip ??
    (status === 'UNKNOWN'
      ? 'Das Datum konnte nicht gelesen werden. Bitte prüfe den Eintrag.'
      : scale === 'hours'
        ? 'Meldefrist an die Aufsichtsbehörde nach Art. 33 Abs. 1 DSGVO (72 Stunden ab Kenntnis)'
        : 'Frist zur Beantwortung nach Art. 12 Abs. 3 DSGVO');

  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        label={label}
        variant={status === 'UNKNOWN' ? 'outlined' : 'filled'}
        sx={{
          backgroundColor: colors.bg,
          color: colors.fg,
          fontWeight: 600,
          borderColor: status === 'UNKNOWN' ? '#CCCCCC' : undefined,
          borderStyle: status === 'UNKNOWN' ? 'dashed' : undefined,
        }}
      />
    </Tooltip>
  );
}
